import { BULK_CASE_ACTION_RUN_STATUS } from '@app/constants/queue';
import { BULK_CASE_ACTION_IN_PROGRESS_TOAST_ID } from '@app/constants/service';
import bulkRunCopy, {
  BULK_RUN_COPY,
  type BulkRunCopy,
} from '@app/helpers/bulk-run-copy';
import i18n, { NAMESPACE } from '@app/i18n';
import type { BulkCaseActionRunComplete } from '@app/services/bulk-case-action';
import { toaster } from '@chakra-primitives/toaster';

interface RunCompleteKeys {
  done: string;
  none: string;
  incomplete: string;
  summary: string;
}

const RUN_COMPLETE_KEYS: Record<BulkRunCopy, RunCompleteKeys> = {
  [BULK_RUN_COPY.QUEUE]: {
    done: 'title',
    none: 'noneAssignedTitle',
    incomplete: 'incompleteTitle',
    summary: 'summary',
  },
  [BULK_RUN_COPY.QUEUE_REMOVE]: {
    done: 'removedTitle',
    none: 'noneRemovedTitle',
    incomplete: 'incompleteTitle',
    summary: 'summary',
  },
  [BULK_RUN_COPY.ASSIGN]: {
    done: 'assignedTitle',
    none: 'noneAssignedUserTitle',
    incomplete: 'incompleteUpdateTitle',
    summary: 'summaryAssigned',
  },
  [BULK_RUN_COPY.UNASSIGN]: {
    done: 'unassignedTitle',
    none: 'noneAssignedUserTitle',
    incomplete: 'incompleteUpdateTitle',
    summary: 'summaryUnassigned',
  },
  [BULK_RUN_COPY.CLOSE]: {
    done: 'closedTitle',
    none: 'noneClosedTitle',
    incomplete: 'incompleteUpdateTitle',
    summary: 'summaryClosed',
  },
};

// The submitter may be anywhere when a run finishes, so the namespace may be unloaded.
export const showBulkCaseActionRunCompleteToast = async (
  run: BulkCaseActionRunComplete,
  onViewDetails: () => void,
): Promise<void> => {
  await i18n.loadNamespaces([NAMESPACE.BULK_CASE_ACTIONS, NAMESPACE.COMMON]);

  const succeeded = run?.succeededCount ?? 0;
  const skipped = run?.skippedCount ?? 0;
  const failed = run?.failedCount ?? 0;
  const unresolved = skipped + failed;
  const ranToCompletion = run?.status === BULK_CASE_ACTION_RUN_STATUS.COMPLETED;

  const type = (() => {
    if (!ranToCompletion) return 'error';
    return unresolved > 0 ? 'warning' : 'success';
  })();

  const keys = RUN_COMPLETE_KEYS[bulkRunCopy(run)];

  const titleKey = (() => {
    if (!ranToCompletion) return keys.incomplete;
    return succeeded === 0 ? keys.none : keys.done;
  })();

  toaster.dismiss(BULK_CASE_ACTION_IN_PROGRESS_TOAST_ID);
  toaster.create({
    type,
    title: i18n.t(`${NAMESPACE.BULK_CASE_ACTIONS}:runComplete.${titleKey}`),
    description: i18n.t(
      `${NAMESPACE.BULK_CASE_ACTIONS}:runComplete.${keys.summary}`,
      { succeeded, skipped, failed },
    ),
    ...(unresolved > 0
      ? {
          action: {
            label: i18n.t(
              `${NAMESPACE.BULK_CASE_ACTIONS}:runComplete.viewDetails`,
            ),
            onClick: onViewDetails,
          },
        }
      : {}),
  });
};

export default showBulkCaseActionRunCompleteToast;
